/**
 * `<policy-fold-order>` — where this row filter lands in the fold (`SC-F512`,
 * `SC-S511`).
 *
 * Row filters **fold order-dependently**. The first policy to attach installs its
 * predicate and each later one attaches with its **own** `combine`, so
 * `A(restrictive)` followed by `B(permissive)` yields `A OR B` — the second policy has
 * undone the first — while the reverse order yields `B AND A`. The order is priority
 * descending, then name ascending, which means a security-relevant difference is
 * produced by an alphabetical tie-break. Nothing about two numbers on a form makes
 * that visible, so this panel makes it visible.
 *
 * It is an advisory mirror of `internal/policy/conflict.go:149-203` (`03` §3.14, M2)
 * and carries the label in its header. It also states the one thing it cannot
 * compute: the fold happens **per table**, among the policies that match that table,
 * and resolving that overlap needs the engine's own matcher against a live schema.
 * So the list is every row filter in the draft in fold order — a superset — and says
 * so rather than implying it has resolved the intersection.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "../../components/index.js";
import { getPolicy, listPolicies } from "../../api/policies.js";
import { asRecord } from "./model.js";
import { foldExpression, type FoldMember } from "./sql-preview.js";

/**
 * loadFoldMembers reads every `RowFilterPolicy` in the draft with its `combine`.
 *
 * The list route answers rows without specs, and `combine` is the field the fold
 * turns on — so each peer is read individually. That is acceptable for the same
 * reason it is acceptable for bindings: a generation holds dozens of objects, not
 * thousands, because the render caps the bundle at thirteen files and one object
 * above 500 KB cannot be part of any renderable bundle.
 *
 * A peer whose spec cannot be read is kept with an empty `combine` rather than
 * dropped. Its position in the order is a fact from the list, and omitting it would
 * understate how many policies fold onto the same table.
 */
export async function loadFoldMembers(
  instanceId: string,
  selfName: string,
): Promise<FoldMember[]> {
  const list = await listPolicies(instanceId, { kind: "RowFilterPolicy", limit: 200 });
  const rows = list.items.filter((r) => r.enabled);
  const specs = await Promise.all(
    rows.map((r) =>
      getPolicy(instanceId, "RowFilterPolicy", r.name)
        .then((o) => asRecord(o.spec)["combine"])
        .catch(() => undefined),
    ),
  );
  return rows.map((r, i) => ({
    name: r.name,
    priority: r.priority,
    combine: typeof specs[i] === "string" ? (specs[i] as string) : "",
    shadow: !!r.shadow,
    self: r.name === selfName,
  }));
}

@customElement("policy-fold-order")
export class PolicyFoldOrder extends LitElement {
  /** Every row filter in the draft, in any order — this element sorts. */
  @property({ attribute: false }) members: FoldMember[] = [];
  /** This policy's name, priority and combine as currently authored, so the panel
   * moves while the author edits rather than showing the last saved state. */
  @property() selfName = "";
  @property({ type: Number }) selfPriority = 0;
  @property() selfCombine = "";
  /** True when this policy is in a shadow mode, so it takes part in the order and
   * changes no outcome. */
  @property({ type: Boolean }) selfShadow = false;
  /** The `Combine` enum's own explanation of `permissive`, from the schema. */
  @property() permissiveHelp = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .advisory {
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      display: block;
      margin-bottom: var(--bino-space-sm);
    }
    ol {
      list-style: none;
      counter-reset: pos;
      margin: 0;
      padding: 0;
      display: grid;
      gap: 0.125rem;
    }
    li {
      counter-increment: pos;
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      padding: 0.125rem 0;
    }
    li::before {
      content: counter(pos) ".";
      font-family: var(--bino-font-mono);
    }
    li.self {
      color: var(--bino-text);
      font-weight: 600;
    }
    .name {
      font-family: var(--bino-font-mono);
    }
    .widens {
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border-radius: var(--bino-radius-sm);
      padding: 0 var(--bino-space-xs);
    }
    .result {
      margin: var(--bino-space-sm) 0 0;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-xs) var(--bino-space-sm);
      overflow-x: auto;
    }
    p {
      margin: var(--bino-space-sm) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    p.warn {
      color: var(--bino-warning-text);
    }
  `;

  /** The members with this policy's live values substituted in, in fold order. */
  private get ordered(): FoldMember[] {
    const others = this.members.filter((m) => m.name !== this.selfName);
    const self: FoldMember = {
      name: this.selfName || "(this policy)",
      priority: this.selfPriority,
      combine: this.selfCombine,
      shadow: this.selfShadow,
      self: true,
    };
    return [...others, self].sort(
      (a, b) => b.priority - a.priority || a.name.localeCompare(b.name),
    );
  }

  override render() {
    const rows = this.ordered;
    const expression = foldExpression(rows);
    const firstActing = rows.find((m) => !m.shadow);
    const widening = rows.filter(
      (m) => m.combine === "permissive" && !m.shadow && m !== firstActing,
    );
    return html`
      <span class="advisory">Advisory preview — the instance's engine decides.</span>
      <ol>
        ${rows.map((m) => {
          const first = m === firstActing;
          return html`<li class=${m.self ? "self" : ""}>
            <span class="name">${m.name}</span>
            <span>priority ${m.priority}</span>
            <span>${m.combine || "combine unknown"}</span>
            ${m.shadow
              ? html`<bn-badge variant="audit">shadow — changes no outcome</bn-badge>`
              : first
                ? html`<span>installs the predicate</span>`
                : m.combine === "permissive"
                  ? html`<span class="widens">OR — widens what the earlier filters allowed</span>`
                  : html`<span>AND</span>`}
          </li>`;
        })}
      </ol>
      ${expression
        ? html`<p class="result">${expression}</p>`
        : html`<p>
            Every row filter in the draft is in a shadow mode, so none of them narrows any query yet.
          </p>`}
      <p>
        The fold happens <strong>per table</strong>, among the row filters that match that table. This
        list is every row filter in the draft in the order they would fold — a superset — because
        resolving which of them meet on one table needs the engine's own matcher against a live
        schema.
      </p>
      ${widening.length > 0
        ? html`<p class="warn">
            ${widening.map((m) => m.name).join(", ")}
            ${widening.length === 1 ? "attaches" : "attach"} permissively, which ORs and therefore
            widens what each earlier filter alone allowed.${this.permissiveHelp
              ? html` ${this.permissiveHelp}`
              : nothing}
          </p>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-fold-order": PolicyFoldOrder;
  }
}
